import { useState } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter 
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Plus, 
  Trash2, 
  GripVertical, 
  Search,
  CheckCircle2
} from 'lucide-react';
import { useGamification, GamificationCampaign } from '@/hooks/useGamification';
import { useQRCodes } from '@/hooks/useQRCodes';
import { ScrollArea } from '@/components/ui/scroll-area';

interface CampaignStepEditorProps {
  campaign: GamificationCampaign;
  onClose: () => void;
}

export function CampaignStepEditor({ campaign, onClose }: CampaignStepEditorProps) {
  const { addStep, removeStep } = useGamification();
  const { qrCodes } = useQRCodes();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedQR, setSelectedQR] = useState<string | null>(null);
  const [unlockMessage, setUnlockMessage] = useState('');

  const filteredQRs = qrCodes.filter(qr => 
    qr.isDynamic && 
    qr.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
    !campaign.steps?.some(step => step.qr_code_id === qr.id)
  );

  const handleAddStep = () => {
    if (!selectedQR) return;

    addStep.mutate({
      campaign_id: campaign.id,
      qr_code_id: selectedQR,
      step_order: (campaign.steps?.length || 0) + 1,
      unlock_message: unlockMessage || 'You found a checkpoint!'
    }, {
      onSuccess: () => {
        setSelectedQR(null);
        setUnlockMessage('');
      }
    });
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Manage Campaign Steps: {campaign.title}</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-4 flex-1 overflow-hidden">
          {/* Current Steps */}
          <div className="flex flex-col space-y-4">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center justify-between">
              Campaign Steps
              <Badge variant="outline">{campaign.steps?.length || 0}</Badge>
            </h3>
            <ScrollArea className="flex-1 border rounded-lg p-2 bg-muted/20">
              <div className="space-y-2">
                {campaign.steps?.sort((a, b) => a.step_order - b.step_order).map((step, idx) => (
                  <Card key={step.id} className="p-3 flex items-center gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-[10px] flex items-center justify-center font-bold">
                      {idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{step.qr_code?.name || 'Deleted QR'}</p>
                      <p className="text-[10px] text-muted-foreground truncate italic">"{step.unlock_message}"</p>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8 text-destructive"
                      onClick={() => removeStep.mutate(step.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </Card>
                ))}
                {(!campaign.steps || campaign.steps.length === 0) && (
                  <div className="text-center py-8 text-xs text-muted-foreground">
                    No steps added yet. Add QR codes from the list.
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>

          {/* Add Steps */}
          <div className="flex flex-col space-y-4">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Add QR Checkpoint</h3>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
              <Input 
                placeholder="Find dynamic QR..." 
                className="h-8 pl-7 text-xs"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>
            
            <ScrollArea className="flex-1 border rounded-lg bg-white">
              <div className="p-1 space-y-1">
                {filteredQRs.map(qr => (
                  <div 
                    key={qr.id}
                    onClick={() => setSelectedQR(qr.id)}
                    className={`p-2 rounded cursor-pointer text-xs flex justify-between items-center transition-colors ${
                      selectedQR === qr.id ? 'bg-primary/10 border-primary/20' : 'hover:bg-muted'
                    }`}
                  >
                    <span className="truncate flex-1">{qr.name}</span>
                    {selectedQR === qr.id && <CheckCircle2 className="h-3 w-3 text-primary ml-2" />}
                  </div>
                ))}
              </div>
            </ScrollArea>

            {selectedQR && (
              <div className="space-y-2 pt-2 border-t">
                <Label className="text-[10px] uppercase font-bold text-muted-foreground">Unlock Message</Label>
                <Input 
                  placeholder="e.g. Clue for next step..." 
                  className="h-8 text-xs"
                  value={unlockMessage}
                  onChange={e => setUnlockMessage(e.target.value)}
                />
                <Button 
                  className="w-full h-8 text-xs" 
                  onClick={handleAddStep}
                  disabled={addStep.isPending}
                >
                  <Plus className="h-3 w-3 mr-1" /> Add Checkpoint
                </Button>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="pt-4 border-t">
          <Button variant="outline" onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
